/**
 * Telegram + test headers for API calls without importing api.ts (avoids circular deps
 * api → userLanguage → cloudStorageSync → onboarding).
 */
import { USER_LANGUAGE_STORAGE_KEY, USER_MANUALLY_SELECTED_FLAG_KEY } from "../i18nStorageKeys";

export const getApiBase = (): string => {
  const origin = window.location.origin;
  return origin.includes("ngrok-free.app") || origin.includes("localhost")
    ? "/api"
    : "https://notapi.web3islands.com";
};

export const getInitData = (): string => {
  const tg: { initData?: string } | undefined = window.Telegram?.WebApp;
  return tg?.initData || "";
};

const getStorage = (name: string): string | undefined => {
  try {
    return localStorage.getItem(name) || undefined;
  } catch {
    return undefined;
  }
};

export const getTestHeaders = (): Record<string, string> => {
  const h: Record<string, string> = {};
  const testUserId = getStorage("test_user_id");
  const tgUserId = getStorage("tg_user_id");
  const botToken = getStorage("bot_token");
  if (testUserId) h["X-TEST-User-Id"] = testUserId;
  if (tgUserId) h["X-TEST-Tg-User-Id"] = tgUserId;
  if (botToken) h["X-TEST-Bot-Token"] = botToken;
  return h;
};

function createAppSessionId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through */
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

/**
 * One id per app launch (page load) — deliberately kept in memory only, never in
 * session/local storage, so closing and reopening the mini-app always yields a
 * new one. The backend binds unfinished onboarding to it: a new session id
 * restarts onboarding from the very first screen.
 */
export const APP_SESSION_ID = createAppSessionId();

/** Shared API headers (Telegram init data, test headers, app session, manual language). */
export function getCommonHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "X-Telegram-InitData": getInitData(),
    "X-Session-Id": APP_SESSION_ID,
    ...getTestHeaders(),
  };
  try {
    if (localStorage.getItem(USER_MANUALLY_SELECTED_FLAG_KEY) === "true") {
      const lng = localStorage.getItem(USER_LANGUAGE_STORAGE_KEY);
      if (lng) {
        headers["X-User-Manually-Selected-Language"] = lng;
      }
    }
  } catch {
    /* ignore */
  }
  return headers;
}

/** @deprecated Use getCommonHeaders — kept for onboarding imports. */
export const getOnboardingFetchHeaders = getCommonHeaders;
